/* Surfaces playground — a 4-zone, build-by-clicking surface composer for a
 * non-technical builder. A surface is one screen, and it IS built from
 * components, so the four zones map directly to "what you do":
 *   1) Live screen   — the composed surface drawn by the REAL renderer.
 *   2) Components     — an + Add palette + the ordered list of what's on screen.
 *   3) Surface options— the screen's own settings (title + task mode).
 *   4) A2UI JSON      — the exact saved object, updating live as you build.
 * Per-component options (button label, field type…) live in the Components
 * playground — here you compose the screen, not tune each part.
 *
 * Flat shapes only (no discriminated unions), every array index guarded, ES5-safe. */
import { useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Layers, Plus, Sliders, SquareStack, X } from "lucide-react";
import { CATALOG } from "../content/catalog";
import { DEFAULTS } from "../content/samples";
import { buildPreviewTree } from "../preview/buildTree";
import { SurfaceStage } from "../preview/SurfaceStage";
import { JsonPanel } from "../playground/JsonPanel";
import type { Component } from "../preview/engine/types";

/* One thing placed on the screen: which component it is, the values seeding its
 * preview (from the catalog-representative DEFAULTS), and — for a Button — its
 * own visible label (Buttons would otherwise all share buildTree's "Click me"). */
interface Item {
  uid: string;
  type: string;
  values: Record<string, unknown>;
  label: string;
}

type Tree = { components: Component[]; rootId: string };

/* A compact, common set for the palette — the components a typical screen needs,
 * each paired with the friendly label shown on its + Add chip. */
const PALETTE: Array<{ type: string; label: string }> = [
  { type: "Text", label: "Text" },
  { type: "TextField", label: "Text field" },
  { type: "Button", label: "Button" },
  { type: "Image", label: "Image" },
  { type: "Divider", label: "Divider" },
  { type: "StatusBadge", label: "Status badge" },
  { type: "CheckBox", label: "Checkbox" },
  { type: "MultipleChoice", label: "Choice" },
];

/* A small, populated default: a sign-in screen. */
const INITIAL: Item[] = [
  { uid: "i0", type: "TextField", values: { label: "Email address", text: "", textFieldType: "shortText" }, label: "" },
  { uid: "i1", type: "TextField", values: { label: "Password", text: "", textFieldType: "shortText" }, label: "" },
  { uid: "i2", type: "CheckBox", values: { label: "Remember me", value: false }, label: "" },
  { uid: "i3", type: "Button", values: { primary: true }, label: "Sign in" },
];

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function heading(id: string, t: string): Component {
  return { id, componentProperties: { Text: { text: { literalString: t }, usageHint: { literalString: "heading" } } } };
}

/* buildPreviewTree reuses the same internal ids on every call, so namespace each
 * item's whole subtree by its uid before merging the items into one screen. */
function remapIds(sub: Tree, prefix: string): Tree {
  const map: Record<string, string> = {};
  for (const c of sub.components) map[c.id] = prefix + "_" + c.id;

  const rewrite = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(rewrite);
    const o = asRecord(v);
    if (o) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(o)) {
        const val = o[k];
        if (k === "componentId" && typeof val === "string") {
          out[k] = map[val] != null ? map[val] : val;
        } else if (k === "explicitList" && Array.isArray(val)) {
          out[k] = val.map((x) => (typeof x === "string" && map[x] != null ? map[x] : x));
        } else {
          out[k] = rewrite(val);
        }
      }
      return out;
    }
    return v;
  };

  const components = sub.components.map((c) => ({
    id: map[c.id] != null ? map[c.id] : c.id,
    componentProperties: rewrite(c.componentProperties) as Record<string, Record<string, unknown>>,
  }));
  return { components, rootId: map[sub.rootId] != null ? map[sub.rootId] : sub.rootId };
}

/* Give a built Button subtree its own label by overwriting the sample child
 * Text that buildTree attached (which would otherwise read "Click me"). */
function applyButtonLabel(tree: Tree, label: string): void {
  const text = label.trim();
  if (!text) return;
  const root = tree.components.find((c) => c.id === tree.rootId);
  if (!root) return;
  const btn = asRecord(root.componentProperties.Button);
  const child = btn ? asRecord(btn.child) : null;
  const childId = child && typeof child.componentId === "string" ? child.componentId : "";
  if (!childId) return;
  const childComp = tree.components.find((c) => c.id === childId);
  if (!childComp) return;
  const textProps = childComp.componentProperties.Text;
  if (textProps) textProps.text = { literalString: text };
}

/* Title heading + every item, stacked in a Column inside a Card — the whole
 * surface, ready for the live renderer. Empty → a friendly placeholder. */
function composeTree(title: string, items: Item[]): Tree {
  const comps: Component[] = [];
  const kids: string[] = [];

  if (title.trim()) {
    comps.push(heading("s_title", title));
    kids.push("s_title");
  }

  for (const it of items) {
    const sub = buildPreviewTree(it.type, CATALOG.components[it.type], it.values);
    const r = remapIds(sub, it.uid);
    if (it.type === "Button") applyButtonLabel(r, it.label);
    for (const c of r.components) comps.push(c);
    kids.push(r.rootId);
  }

  if (kids.length === 0) {
    comps.push({
      id: "s_empty",
      componentProperties: { Text: { text: { literalString: "Empty screen — add a component from the palette." }, usageHint: { literalString: "caption" } } },
    });
    kids.push("s_empty");
  }

  comps.push({ id: "s_col", componentProperties: { Column: { alignment: { literalString: "stretch" }, children: { explicitList: kids } } } });
  comps.push({ id: "s_root", componentProperties: { Card: { child: { componentId: "s_col" } } } });
  return { components: comps, rootId: "s_root" };
}

/* slug for the surface id, derived from the title. */
function slug(raw: string, fallback: string): string {
  const s = raw.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
  return s || fallback;
}

const zoneHead: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8 };
const hint: React.CSSProperties = { fontSize: 12, color: "var(--gd-muted)", lineHeight: 1.5, margin: "0 0 12px" };
const groupLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
  color: "var(--gd-muted)", margin: "0 0 7px",
};
const orderNum: React.CSSProperties = {
  width: 18, textAlign: "right", opacity: 0.5, fontFamily: "var(--gd-mono)", fontSize: 12, flex: "none",
};

function Toggle({ on, set }: { on: boolean; set: (b: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} className={"gd-toggle" + (on ? " on" : "")} onClick={() => set(!on)}>
      <span className="gd-toggle-dot" />
    </button>
  );
}

export function SurfacesPlayground() {
  const seq = useRef(INITIAL.length);
  const [title, setTitle] = useState("Sign in");
  const [task, setTask] = useState(false);
  const [items, setItems] = useState<Item[]>(INITIAL);
  const [selected, setSelected] = useState<string | null>("i3");

  function add(type: string) {
    const uid = "i" + seq.current++;
    const label = type === "Button" ? "Button" : "";
    setItems((prev) => [...prev, { uid, type, values: { ...(DEFAULTS[type] ?? {}) }, label }]);
    setSelected(uid);
  }
  function remove(uid: string) {
    setItems((prev) => prev.filter((it) => it.uid !== uid));
    setSelected((cur) => (cur === uid ? null : cur));
  }
  function move(i: number, dir: -1 | 1) {
    setItems((prev) => {
      const j = i + dir;
      if (i < 0 || i >= prev.length || j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      const a = next[i];
      const b = next[j];
      if (!a || !b) return prev;
      next[i] = b;
      next[j] = a;
      return next;
    });
  }

  const tree = useMemo(() => composeTree(title, items), [title, items]);

  /* The saved surface — content holds the live a2ui tree, so this is the real
   * object and updates in lockstep with the composition. */
  const surface = useMemo(() => {
    const obj: Record<string, unknown> = { id: slug(title, "screen_1") };
    if (title.trim()) obj.title = title;
    obj.content = { rootId: tree.rootId, components: tree.components };
    if (task) obj.mode = { type: "task" };
    return obj;
  }, [title, tree, task]);

  return (
    <div className="gd-section">
      <div className="gd-section-intro">
        <h2>Surfaces</h2>
        <p>
          A <strong>surface</strong> is one screen. You build it by dropping in components, ordering
          them, and giving the screen a couple of settings — that's it. Add and reorder on the left,
          watch the real renderer draw it in the middle, and the surface options + A2UI JSON on the
          right stay in sync. To tune a single component (a button's label, a field's type…), use the
          <em>Components</em> playground.
        </p>
      </div>

      <div className="gd-z3">
        {/* LEFT — components on the screen */}
        <div className="gd-zone">
          <div className="gd-col-head" style={zoneHead}>
            <Layers size={14} /> Components on the screen
          </div>
          <div className="gd-zone-body">
            <div style={groupLabel}>Add a component</div>
            <div className="gd-chips" style={{ marginBottom: 16 }}>
              {PALETTE.map((p) => (
                <button key={p.type} type="button" className="gd-addbtn" onClick={() => add(p.type)} title={"Add a " + p.label}>
                  <Plus size={12} style={{ marginRight: 4, verticalAlign: "-1px" }} />
                  {p.label}
                </button>
              ))}
            </div>

            <div style={{ ...groupLabel, display: "flex", alignItems: "baseline", gap: 7 }}>
              On the screen
              <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                {items.length} component{items.length === 1 ? "" : "s"} · top to bottom
              </span>
            </div>
            {items.length === 0 ? (
              <div className="gd-closed" style={{ display: "inline-block" }}>empty — add one above</div>
            ) : (
              <div className="gd-rowlist">
                {items.map((it, i) => (
                  <div
                    key={it.uid}
                    className={"gd-listrow" + (selected === it.uid ? " sel" : "")}
                    onClick={() => setSelected(it.uid)}
                    style={{ cursor: "pointer" }}
                    title="Select"
                  >
                    <span style={orderNum}>{i + 1}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "var(--gd-mono)" }}>
                      {it.type}
                      {it.type === "Button" && it.label ? (
                        <span style={{ color: "var(--gd-muted)" }}> · {it.label}</span>
                      ) : null}
                    </span>
                    <button type="button" className="gd-iconbtn" disabled={i === 0} style={{ opacity: i === 0 ? 0.35 : 1 }} aria-label="Move up" title="Move up" onClick={(e) => { e.stopPropagation(); move(i, -1); }}>
                      <ArrowUp size={13} />
                    </button>
                    <button type="button" className="gd-iconbtn" disabled={i === items.length - 1} style={{ opacity: i === items.length - 1 ? 0.35 : 1 }} aria-label="Move down" title="Move down" onClick={(e) => { e.stopPropagation(); move(i, 1); }}>
                      <ArrowDown size={13} />
                    </button>
                    <button type="button" className="gd-iconbtn" aria-label="Remove" title="Remove" onClick={(e) => { e.stopPropagation(); remove(it.uid); }}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* MIDDLE — the live screen */}
        <div className="gd-zone">
          <div className="gd-col-head" style={zoneHead}>
            <SquareStack size={14} /> Live screen{task ? " · task" : ""}
          </div>
          <div className="gd-zone-body gd-zone-stage">
            <SurfaceStage components={tree.components} rootId={tree.rootId} />
          </div>
        </div>

        {/* RIGHT — surface options (top) + A2UI JSON (below) */}
        <div className="gd-z3-col">
          <div className="gd-zone">
            <div className="gd-col-head" style={zoneHead}>
              <Sliders size={14} /> Surface options
            </div>
            <div className="gd-zone-body">
              <div className="gd-ctl" style={{ paddingTop: 0 }}>
                <div className="gd-ctl-head">
                  <span className="gd-ctl-name">title</span>
                  <span className="gd-ctl-type">heading shown above the screen</span>
                </div>
                <input className="gd-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sign in" />
              </div>

              <div className="gd-ctl" style={{ borderBottom: 0, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <div className="gd-ctl-name">task mode</div>
                  <div className="gd-ctl-type" style={{ marginTop: 4, lineHeight: 1.5 }}>
                    Turn this screen into a task assigned to someone — the run pauses until they act on it.
                  </div>
                </div>
                <Toggle on={task} set={setTask} />
              </div>

              <p style={{ ...hint, margin: "4px 0 0", fontStyle: "italic" }}>
                These are the surface's own settings. A component's options live in the Components playground.
              </p>
            </div>
          </div>

          <div className="gd-zone">
            <JsonPanel value={surface} title="A2UI JSON" />
          </div>
        </div>
      </div>
    </div>
  );
}
